import { Box, VStack, Stack } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { ScrollText } from 'lucide-react';
import { useSnapshot } from 'valtio';

import { CustomStepper } from '@duke-hq/ui/components/StepWizard/CustomStepper';
import { useStep } from '@duke-hq/ui/components/StepWizard/useStep';

import {
  internalEnrolmentState,
  SelectedMemberType,
} from '@duke-hq/app-provider-web/tenant/tenantState/InternalEnrolmentState';
import { SelectParticipant } from '@duke-hq/app-provider-web/tenant/components/InternalEnrolmentForms/SelectParticipant';
import { SelectSkillSet } from '@duke-hq/app-provider-web/tenant/components/InternalEnrolmentForms/SelectSkillSet';
import { ReviewCourse } from '@duke-hq/app-provider-web/tenant/components/InternalEnrolmentForms/ReviewCourse';
import { SkillSetSettings } from '@duke-hq/app-provider-web/tenant/components/InternalEnrolmentForms/SkillSetSettings';
import { SkillSetSummary } from '@duke-hq/app-provider-web/tenant/components/InternalEnrolmentForms/SkillSetSummary';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { useTenantContext } from '../TenantContext';

interface StepComponentProps {
  goToNextStep: () => void;
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
  selectedMember: SelectedMemberType;
}

interface EnrolmentStepConfig {
  title: () => string;
  key: string;
  component: React.ComponentType<StepComponentProps>;
}

const ENROLMENT_STEPS: EnrolmentStepConfig[] = [
  {
    title: () => t`Select Participant`,
    key: 'SelectParticipant',
    component: SelectParticipant,
  },
  {
    title: () => t`Select Course`,
    key: 'SelectCourse',
    component: SelectSkillSet,
  },
  {
    title: () => t`Review Course`,
    key: 'ReviewCourse',
    component: ReviewCourse,
  },
  {
    title: () => t`Settings`,
    key: 'EnrolmentSettings',
    component: SkillSetSettings,
  },
  {
    title: () => t`Summary`,
    key: 'Summary',
    component: SkillSetSummary,
  },
];

function EnrolmentSteps({
  currentStep,
  goToNextStep,
  goToPrevStep,
  setStep,
  selectedMember,
}: {
  currentStep: number;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
  selectedMember: SelectedMemberType;
}) {
  const stepConfig = ENROLMENT_STEPS[currentStep];

  if (!stepConfig) {
    return <Box>Error: Invalid Step</Box>;
  }

  const { key, component: Step } = stepConfig;

  const stepProps: StepComponentProps = {
    goToNextStep,
    goToPrevStep,
    setStep,
    selectedMember: {
      id: '',
      first_name: '',
      last_name: '',
      email: '',
      role: '',
    },
  };

  if (key === 'SelectParticipant') {
    stepProps.selectedMember = selectedMember;
  }

  return <Step key={key} {...stepProps} />;
}

function EnrolmentModalContent({ selectedMember }: { selectedMember: SelectedMemberType }) {
  const { currentStep, setStep, goToNextStep, goToPrevStep } = useStep({
    maxStep: ENROLMENT_STEPS.length - 1,
    initialStep: 0,
  });

  return (
    <VStack w="full" h="full" justify="start" gap="16px">
      <Box w="full">
        <Stack spacing="0" direction={{ base: 'column', md: 'row' }}>
          {ENROLMENT_STEPS.map((step, id) => (
            <CustomStepper
              key={step.key}
              cursor="pointer"
              onClick={() => {
                if (currentStep > id) {
                  setStep(id);
                }
              }}
              title={step.title()}
              isActive={currentStep === id}
              isCompleted={currentStep > id}
              isFirstStep={id === 0}
              isLastStep={ENROLMENT_STEPS.length === id + 1}
            />
          ))}
        </Stack>
      </Box>

      <Box w="full" h="full">
        <EnrolmentSteps
          currentStep={currentStep}
          goToNextStep={goToNextStep}
          goToPrevStep={goToPrevStep}
          setStep={setStep}
          selectedMember={selectedMember}
        />
      </Box>
    </VStack>
  );
}

export function TeamInternalEnrolmentFormModal({
  selectedMember,
}: {
  selectedMember?: SelectedMemberType;
}) {
  const snap = useSnapshot(internalEnrolmentState);
  const { openModal, closeModal, memberForEnrolment } = snap;
  const tenantCtx = useTenantContext();

  if (
    tenantCtx != null &&
    tenantCtx.permissions.includes('canCreateInternalEnrolments') === false
  ) {
    return null;
  }

  return (
    <FormModalWrapper
      modalOpenButtonText={t`Create internal enrolment`}
      modalOpenButtonLeftIcon={ScrollText}
      modalContent={<EnrolmentModalContent selectedMember={memberForEnrolment} />}
      isOpen={snap.isModalOpen}
      onClose={closeModal}
      onOpen={() => openModal(selectedMember)}
      modalContentHeight="700px"
      bgColor="bg.surface"
    />
  );
}
